// The Global Settings form: heading, schema sections, metadata and conditions,
// the feature panels, and the save actions.

import React from 'react';
import PropTypes from 'prop-types';
import Pencil from '@decisiv/iconix/lib/components/Pencil';
import { Button, Flex, Grid } from '@decisiv/ui-components';
import { H3, P } from '@decisiv/ui-components/lib/components/Typography';
import { t, Trans } from '@lingui/macro';

import CommandIconAndTitle from '../../../components/CommandIconAndTitle';
import { SETTINGS_PAGE_INPUT_PAYLOAD_TEXT } from '../../../common/constants/staticTexts';
import GroupedStaticAttributesSection from '../GroupedStaticAttributesSection';
import { IntegrationForm, SectionRow } from '../StyledComponents';
import MetadataAndConditions from './MetadataAndConditions';
import SettingsPanels from './SettingsPanels';

const SettingsFormBody = ({
  integrationName = '',
  edit,
  errors,
  canEdit = false,
  onEdit,
  onOpenSchemaDetails,
  groupedStaticAttributes,
  renderField,
  metadataAndConditions,
  panels,
  showCacheSettings = false,
  showAutomaticRetrySettings = false,
  onToggleCacheModal,
  onShowTtlField,
  saveDisabled = false,
  onSave,
  onCancel,
}) => (
  <IntegrationForm>
    <Grid.Container>
      <SectionRow>
        <Grid.Column span="10">
          <Flex>
            <Flex flexDirection="column">
              <H3>{t`Global Settings`}</H3>
              <P shade={1} size="small">
                <Trans>Configure the global settings for</Trans>
                &nbsp;&lt;{integrationName}&gt;&nbsp;
                <Trans>integration.</Trans>
              </P>
            </Flex>
          </Flex>
        </Grid.Column>
        {canEdit && edit && (
          <Grid.Column span="2">
            <Flex justifyContent="right">
              <Button
                icon={Pencil}
                text={t`Edit`}
                name={t`edit`}
                kind="secondary"
                onClick={onEdit}
              />
            </Flex>
          </Grid.Column>
        )}
      </SectionRow>

      <SectionRow key="InputPayload">
        <Grid.Column span="2">
          <CommandIconAndTitle
            title={t`InputPayload`}
            onClick={() => onOpenSchemaDetails(t`InputPayload`)}
          />
        </Grid.Column>
        <Grid.Column>
          <P shade={1} style={{ marginTop: 7 }}>
            <Trans>{SETTINGS_PAGE_INPUT_PAYLOAD_TEXT}</Trans>
          </P>
        </Grid.Column>
      </SectionRow>

      <GroupedStaticAttributesSection
        groupedStaticAttributes={groupedStaticAttributes}
        handleSchemaDetailsModalOpen={onOpenSchemaDetails}
        decideFormElement={renderField}
      />

      <MetadataAndConditions
        edit={edit}
        errors={errors}
        {...metadataAndConditions}
      />

      <SettingsPanels
        edit={edit}
        errors={errors}
        panels={panels}
        showCacheSettings={showCacheSettings}
        showCircuitBreakerSettings
        showAutomaticRetrySettings={showAutomaticRetrySettings}
        onToggleCacheModal={onToggleCacheModal}
        onShowTtlField={onShowTtlField}
      />

      {!edit && (
        <SectionRow>
          <Flex marginTop={2}>
            <Button
              name={t`save;`}
              text={t`Save`}
              disabled={saveDisabled}
              onClick={onSave}
            />
            <Button
              style={{ marginLeft: '0.4rem' }}
              name={t`cancel`}
              text={t`Cancel`}
              kind="secondary"
              onClick={onCancel}
            />
          </Flex>
        </SectionRow>
      )}
    </Grid.Container>
  </IntegrationForm>
);

SettingsFormBody.propTypes = {
  integrationName: PropTypes.string,
  edit: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  errors: PropTypes.object.isRequired,
  canEdit: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onOpenSchemaDetails: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  groupedStaticAttributes: PropTypes.object.isRequired,
  renderField: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  metadataAndConditions: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  panels: PropTypes.object.isRequired,
  showCacheSettings: PropTypes.bool,
  showAutomaticRetrySettings: PropTypes.bool,
  onToggleCacheModal: PropTypes.func.isRequired,
  onShowTtlField: PropTypes.func.isRequired,
  saveDisabled: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
export default SettingsFormBody;
